/**
 * Structure: the delivery tree. Table is the edit view; org chart is view only.
 * Counts, never ranks (docs/2026-09-20-program-structure.md).
 */
import { useEffect, useState } from "preact/hooks";
import { plural } from "@trellis/core";
import type { AskLine, StructureView, StructureUnit } from "@trellis/core";
import { VipEditor, type VipLine } from "./vips.js";
import { TopicEditor, type TopicLine } from "./topics.js";
import { AskAssign, CountStrip } from "./program.js";
import { Picker } from "../components/picker.js";

/** How the contract runs one cadence on one unit (docs/2026-09-26-cadence-evidence-link.md). */
export interface UnitCadenceLine {
  unitId: string;
  cadenceId: string;
  weekdays: number[] | null;
  linkKind: string | null;
  joinWebUrl: string | null;
  organiserUpn: string | null;
  sourceUrl: string | null;
  /** Whether Graph has found the meeting this link names yet. */
  resolved: boolean;
  checkedAt: string | null;
  lastError: string | null;
}

export interface StructurePage extends StructureView {
  optOuts: { unitId: string; cadenceId: string }[];
  vips: Record<string, VipLine[]>;
  members: { unitId: string; upn: string }[];
  /** `needsLink`: this cadence opens nothing on a unit until somebody says where its evidence is. */
  eligibleCadences: { id: string; name: string; needsLink?: boolean; eligibleKinds?: string[]; frequency?: string }[];
  programOptOuts: string[];
  programVips: VipLine[];
  people: { upn: string; name: string }[];
  unitCadences?: UnitCadenceLine[];
  /** Only an opted-in organiser's meetings are readable, so only they may be named on a link. */
  meetingsOptIn?: string[];
  /** Asks on no unit: a contract fact, and this page is where one gets placed. */
  unclassifiedIntake?: AskLine[];
}

export interface StructureActions {
  notice?: { text: string; error?: boolean } | undefined;
  onCreate: (input: { kind: "workstream" | "team"; parentId: string; name: string; deliveryLead: string; technicalLead?: string }) => Promise<void>;
  onRole: (unitId: string, roleId: string, upn: string) => Promise<void>;
  onAddPerson: (unitId: string, upn: string) => Promise<void>;
  onRemovePerson: (unitId: string, upn: string) => Promise<void>;
  onVips: (unitId: string, entries: VipLine[]) => Promise<void>;
  onProgramVips?: (entries: VipLine[]) => Promise<void>;
  onProgramOptOut?: (cadenceId: string, optedOut: boolean) => Promise<void>;
  /** Loaded beside the view, not part of it: unit id to its own topics, fetched when a unit is opened. */
  unitTopics?: Record<string, TopicLine[]>;
  onUnitSelected?: (unitId: string) => void;
  onUnitTopics?: (unitId: string, entries: TopicLine[]) => Promise<void>;
  programTopics?: TopicLine[];
  onProgramTopics?: (entries: TopicLine[]) => Promise<void>;
  onOptOut: (unitId: string, cadenceId: string, optedOut: boolean) => Promise<void>;
  onMove: (unitId: string, clinId: string) => Promise<void>;
  onRename?: (unitId: string, name: string) => Promise<void>;
  onClose?: (unitId: string) => Promise<void>;
  onAssign?: (unitId: string, commitmentId: string, to: string) => Promise<void>;
}

/** A person's address is the second line, so it is obvious which directory identity this is. */
/** Everything but a CLIN is a label a lead chose, Unassigned included, so it can be changed. */
function RenameUnit({ unit, onRename }: { unit: StructureUnit; onRename: (unitId: string, name: string) => Promise<void> }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(unit.name);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setName(unit.name); setEditing(false); }, [unit.id, unit.name]);
  if (!editing) {
    return (
      <div class="body" style="font-weight:600;display:flex;gap:10px;align-items:baseline">
        <span>{unit.name}</span>
        <button class="link" onClick={() => setEditing(true)}>Rename</button>
      </div>
    );
  }
  const save = () => { setBusy(true); void onRename(unit.id, name.trim()).finally(() => { setBusy(false); setEditing(false); }); };
  return (
    <div class="inline-form">
      <label class="sr-only" for={`rename-${unit.id}`}>Name</label>
      <input id={`rename-${unit.id}`} value={name} onInput={(e) => setName((e.target as HTMLInputElement).value)}
        onKeyDown={(e) => { if (e.key === "Enter" && name.trim()) save(); if (e.key === "Escape") { setName(unit.name); setEditing(false); } }} />
      <button class="btn" disabled={busy || !name.trim() || name.trim() === unit.name} onClick={save}>Save</button>
      <button class="link" onClick={() => { setName(unit.name); setEditing(false); }}>Cancel</button>
    </div>
  );
}

function peopleOptions(people: { upn: string; name: string }[]) {
  return people.map((p) => ({ value: p.upn, label: p.name, hint: p.upn }));
}

// The holding unit carries the programme's own name and sits at the root, so calling it
// "Unassigned" reads as a stray workstream. It is the programme; what is unassigned is
// anything still hanging off it instead of a CLIN, and the row below says that.
const KIND: Record<StructureUnit["kind"], string> = { clin: "CLIN", unassigned: "Program", workstream: "Workstream", team: "Team" };

export function StructureScreen({ view, actions, viewer }: { view: StructurePage; actions: StructureActions; viewer?: string | undefined }) {
  const [mode, setMode] = useState<"table" | "org">("table");
  const [selected, setSelected] = useState<string | undefined>(undefined);
  const unit = view.units.find((u) => u.id === selected);
  const childrenOf = (id: string | null) => view.units.filter((u) => u.parentId === id);
  // A workstream on the holding unit is funded by no CLIN, which is a finding rather than
  // a blank: nobody can say whether that work is covered.
  const parentKindOf = (u: StructureUnit) => view.units.find((p) => p.id === u.parentId)?.kind;
  // The contract lines in number order, then the holding unit. Unsorted, the tree came
  // back in whatever order the rows happened to sit in, so renaming one line reordered
  // the whole page and the tree disagreed with the funding tile about which line is first.
  // Numeric, so an unpadded CorpDash key sorts 2 before 10 rather than after it.
  const byLine = (a: StructureUnit, b: StructureUnit) =>
    a.key.localeCompare(b.key, undefined, { numeric: true }) || a.name.localeCompare(b.name);
  const roots = view.units.filter((u) => !u.parentId).slice()
    .sort((a, b) => Number(a.kind === "unassigned") - Number(b.kind === "unassigned") || byLine(a, b));
  const walk = (parentId: string | null, depth: number): { unit: StructureUnit; depth: number }[] =>
    childrenOf(parentId).flatMap((u) => [{ unit: u, depth }, ...walk(u.id, depth + 1)]);
  const rows = roots.flatMap((r) => [{ unit: r, depth: 0 }, ...walk(r.id, 1)]);

  // Sorted the same way the rows are, so the hue a line gets here is the hue the funding
  // tile gives it. Off the raw order, line 2 could be first on one and second on the other.
  const clins = view.units.filter((u) => u.kind === "clin").slice().sort(byLine);
  // Which line funds a row, so the tree can draw the spine down its branch. A CLIN is a
  // root, so the root of a row's branch is the answer.
  const rootOf = (u: StructureUnit): StructureUnit | undefined => {
    let cur: StructureUnit | undefined = u;
    while (cur?.parentId) cur = view.units.find((p) => p.id === cur!.parentId);
    return cur;
  };
  /** The line that funds a unit, and which of the two line hues draws it. */
  const lineOf = (u: StructureUnit): { key: string; tone: "a" | "b" } | undefined => {
    const root = rootOf(u);
    if (!root || root.kind !== "clin" || root.id === u.id) return undefined;
    return { key: root.key, tone: clins.indexOf(root) % 2 === 0 ? "a" : "b" };
  };
  const spineOf = (u: StructureUnit, depth: number): string => {
    if (depth === 0) return "";
    const line = lineOf(u);
    return line ? ` spine-${line.tone}` : " spine-none";
  };

  return (
    <main class="main program">
      <div class="contract-head">
        <p class="note dim"><a class="link" href={`/app/programs/${encodeURIComponent(view.programId)}`}>{view.programName}</a></p>
        <div class="contract-title">
          <h1 class="h-lg">Structure</h1>
          <p class="note dim code">{view.ruleVersion}</p>
        </div>
        <p class="lede">
          Contract line, workstream, team, sub-team.{" "}
          {view.card.missingDeliveryLeads
            ? `${plural(view.card.missingDeliveryLeads, "unit")} ${view.card.missingDeliveryLeads === 1 ? "has" : "have"} no delivery lead.`
            : "Every unit has a delivery lead."}
          {view.card.billedWithoutWorkstream ? ` ${plural(view.card.billedWithoutWorkstream, "person", "people")} billing to the contract sit on no workstream.` : ""}
        </p>
        {actions.notice && <div class={`notice${actions.notice.error ? " error" : ""}`}>{actions.notice.text}</div>}
      </div>

      <CountStrip cells={[
        { n: String(clins.length), t: plural(clins.length, "contract line").replace(/^\d+ /, ""), what: "Contract lines on this contract, read from CorpDash. Trellis does not create one." },
        { n: String(view.card.workstreams), t: "workstreams", what: "Units directly under a contract line, or under the holding unit when nobody has put them on a line." },
        { n: String(view.card.teams), t: "teams", what: "Units under a workstream. A team can hold sub-teams." },
        { n: String(view.card.missingDeliveryLeads), t: "with no delivery lead", what: "Units nobody is accountable for. The org chart draws these as a break in the line.", watch: view.card.missingDeliveryLeads > 0 },
      ]} />

      <div class="strip">
        <span class="seg" role="group" aria-label="How to show the structure">
          <button type="button" class={mode === "table" ? "on" : ""} aria-pressed={mode === "table"} onClick={() => setMode("table")}>Tree</button>
          <button type="button" class={mode === "org" ? "on" : ""} aria-pressed={mode === "org"} onClick={() => setMode("org")}>Org chart</button>
        </span>
        <span class="grow" />
        <span class="note dim">{mode === "org" ? "View only. A person on two teams appears twice." : "Select a row to edit it."}</span>
      </div>

      {mode === "org" ? (
        <Org units={view.units} lineOf={lineOf} />
      ) : (
        <div class="split">
          <section class="panel">
            <div class="panel-head">
              <span class="label">Tree</span>
              <span class="grow" />
              <span class="note dim">{plural(rows.length, "unit")}</span>
            </div>
            {rows.map(({ unit: u, depth }) => (
              <button type="button" class={`line tree-row${spineOf(u, depth)}${selected === u.id ? " on" : ""}`} key={u.id} style={`padding-left:${8 + depth * 16}px`} onClick={() => { setSelected(u.id); actions.onUnitSelected?.(u.id); }}>
                <span class={`dot ${u.missingDeliveryLead ? "watch" : u.kind === "clin" || u.kind === "unassigned" ? "navy" : "good"}`} />
                <span class="body">{u.name}<span class="s">{[
                  KIND[u.kind],
                  u.kind === "clin" ? "from CorpDash" : "",
                  u.kind === "workstream" && parentKindOf(u) === "unassigned" ? "under no CLIN" : "",
                  u.deliveryLead ? u.deliveryLead.name : "",
                  u.technicalLead ? `TL ${u.technicalLead.name}` : "",
                  `${u.people} ${u.people === 1 ? "person" : "people"}`,
                ].filter(Boolean).join(" · ")}</span></span>
              </button>
            ))}
            {rows.length === 0 && <div class="line"><span class="dot off" /><span class="body muted">No units yet. Seed creates CLINs and Unassigned.</span></div>}
          </section>
          <aside class="panel">
            {/* Keyed on the unit: without it the inspector's own form state — the role
                picker, the add-person picker, a half-typed child name — survives a
                change of selection and shows the last unit's answers on the next one. */}
            {unit
              ? <Inspector key={unit.id} unit={unit} view={view} actions={actions} {...(viewer ? { viewer } : {})} />
              : <>
                  <div class="panel-head"><span class="label">No unit selected</span></div>
                  <p class="note dim">Choose a row on the left to name its leads, add people, move it to a contract line or close it.</p>
                </>}
          </aside>
        </div>
      )}

      {/* Below the tree and folded away: a scope somebody edits occasionally should not
          sit above the thing they came to read, least of all as an empty form. */}
      {actions.onProgramTopics && (
        <details class="fold panel" style="margin-top:16px">
          <summary class="panel-head">
            <span class="label">Topics on this program</span>
            <span class="grow" />
            <span class="hint">
              {actions.programTopics?.length
                ? plural(actions.programTopics.length, "topic")
                : "None yet · a topic is read whoever sent it"}
            </span>
          </summary>
          <TopicEditor entries={actions.programTopics ?? []} scope="program" onSave={actions.onProgramTopics} />
        </details>
      )}

      {actions.onProgramVips && (
        <details class="fold panel" style="margin-top:16px">
          <summary class="panel-head">
            <span class="label">VIPs on this program</span>
            <span class="grow" />
            <span class="hint">
              {view.programVips.length
                ? plural(view.programVips.length, "person")
                : "None yet · the customer register is already read"}
            </span>
          </summary>
          <p class="note dim" style="padding:0 16px">Read on every unit of this contract. The
            customer register is counted already, so this is for somebody inside Samtek whose
            mail should always be read here.</p>
          <VipEditor entries={view.programVips} kindDefault="internal" internalOnly onSave={actions.onProgramVips} />
        </details>
      )}

      {actions.onProgramOptOut && view.eligibleCadences.length > 0 && (
        <details class="fold panel" style="margin-top:16px">
          <summary class="panel-head">
            <span class="label">Cadences on this program</span>
            <span class="grow" />
            <span class="hint">
              {view.programOptOuts.length
                ? `${plural(view.programOptOuts.length, "cadence")} off`
                : "All running"}
            </span>
          </summary>
          <p class="note dim" style="padding:0 16px">Switching one off here stops it on every unit,
            including units created later. A unit can still opt out on its own account.</p>
          <div style="padding:0 16px 16px">
            {view.eligibleCadences.map((c) => (
              <label class="s" key={c.id} style="display:block;margin:8px 0">
                <input type="checkbox" checked={view.programOptOuts.includes(c.id)} onChange={(e) => void actions.onProgramOptOut!(c.id, (e.target as HTMLInputElement).checked)} /> {c.name}
              </label>
            ))}
          </div>
        </details>
      )}
    </main>
  );
}

function Inspector({ unit, view, actions, viewer }: { unit: StructureUnit; view: StructurePage; actions: StructureActions; viewer?: string | undefined }) {
  const people = view.people;
  const sitting = view.members.filter((m) => m.unitId === unit.id);
  const onUnit = new Set(sitting.map((m) => m.upn));
  const available = people.filter((p) => !onUnit.has(p.upn));
  const nameOf = (upn: string) => people.find((p) => p.upn === upn)?.name ?? upn;
  const clins = view.units.filter((u) => u.kind === "clin");
  // A team may hold teams, so the tree is as deep as the programme needs.
  const canAddChild = true;
  const childKind = unit.kind === "workstream" || unit.kind === "team" ? "team" : "workstream";
  const opted = new Set(view.optOuts.filter((o) => o.unitId === unit.id).map((o) => o.cadenceId));

  return (
    <>
      <div class="panel-head"><span class="label">{KIND[unit.kind]}</span></div>
      {actions.onRename && unit.kind !== "clin"
        ? <RenameUnit unit={unit} onRename={actions.onRename} />
        : <div class="body" style="font-weight:600">{unit.name}</div>}
      <div class="s" style="margin:6px 0 12px">{unit.people} {unit.people === 1 ? "person" : "people"}{unit.missingDeliveryLead ? " · delivery lead required" : ""}</div>
      <div style="margin:0 0 12px">
        <a class="link" href={`/app/programs/${encodeURIComponent(view.programId)}/units/${encodeURIComponent(unit.id)}`}>Open This week</a>
      </div>

      {(unit.kind === "workstream" || unit.kind === "team") && (
        <>
          <span class="label">Roles</span>
          <RolePick label="Delivery lead" value={unit.deliveryLead?.upn ?? ""} people={people} onSave={(upn) => actions.onRole(unit.id, "delivery-lead", upn)} />
          <RolePick label="Technical lead" value={unit.technicalLead?.upn ?? ""} people={people} optional onSave={(upn) => actions.onRole(unit.id, "technical-lead", upn)} />
          <AddPerson people={available} onAdd={(upn) => actions.onAddPerson(unit.id, upn)} />
          {sitting.length > 0 && (
            <div style="margin:8px 0 12px">
              {sitting.map((m) => (
                <div class="s" key={m.upn} style="display:flex;gap:10px;align-items:baseline">
                  <span>{nameOf(m.upn)}</span>
                  <button class="link" onClick={() => void actions.onRemovePerson(unit.id, m.upn)}>Remove</button>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {(unit.kind === "workstream" || unit.kind === "team") && actions.onAssign && (view.unclassifiedIntake ?? []).length > 0 && (
        <div style="margin:16px 0">
          <span class="label">On no unit · name a person to place it here</span>
          {(view.unclassifiedIntake ?? []).map((a) => (
            <div class="s ask-row" key={a.id}>
              <div class="ask-head">
                <span>{a.title}</span>
                {!a.acknowledged && !a.ackWithinStandard && <span class="pill bad">Past the {a.ackStandard} standard</span>}
              </div>
              <div class="muted">{a.detail}</div>
              <div class="muted">{[a.from, a.topics?.length ? `touches ${a.topics.join(", ")}` : ""].filter(Boolean).join(" · ")}</div>
              {viewer && a.ownerUpn === viewer
                ? <AskAssign people={people} onAssign={(to) => actions.onAssign!(unit.id, a.id, to)} label="Assign onto this unit…" hint="They accept before it enters their record. This places the ask on this unit." />
                : <div class="muted">Held by the intake owner.</div>}
            </div>
          ))}
        </div>
      )}

      {canAddChild && <AddChild kind={childKind} parentId={unit.id} people={people} onCreate={actions.onCreate} />}

      {unit.kind === "workstream" && clins.length > 0 && (
        <MoveClin unitName={unit.name} clins={clins} currentParent={unit.parentId} onMove={(clinId) => actions.onMove(unit.id, clinId)} />
      )}

      {(unit.kind === "workstream" || unit.kind === "team") && view.eligibleCadences.length > 0 && (
        <div style="margin-top:16px">
          <span class="label">Cadence opt-out</span>
          {view.eligibleCadences.map((c) => {
            // Off for the whole contract: show it off, say why, and do not offer a switch
            // that would write a unit row and change nothing anybody can see.
            const wide = view.programOptOuts.includes(c.id);
            return (
              <label class="s" key={c.id} style="display:block;margin:6px 0">
                <input type="checkbox" checked={wide || opted.has(c.id)} disabled={wide} onChange={(e) => void actions.onOptOut(unit.id, c.id, (e.target as HTMLInputElement).checked)} /> {c.name}
                {wide && <span class="muted"> · off for the whole contract</span>}
              </label>
            );
          })}
        </div>
      )}

      <div style="margin-top:16px">
        <span class="label">VIPs on this unit</span>
        <VipEditor key={unit.id} entries={view.vips[unit.id] ?? []} kindDefault="internal" onSave={(entries) => actions.onVips(unit.id, entries)} />
      </div>
      {actions.onClose && (unit.kind === "workstream" || unit.kind === "team") && <CloseUnit unit={unit} onClose={actions.onClose} />}

      {actions.onUnitTopics && (
        <div style="margin-top:16px">
          <span class="label">Topics on this unit</span>
          <TopicEditor key={`t-${unit.id}`} entries={actions.unitTopics?.[unit.id] ?? []} scope="unit" onSave={(entries) => actions.onUnitTopics!(unit.id, entries)} />
        </div>
      )}
    </>
  );
}

/** Closing keeps the history: commitments keep the unit they were classified onto. */
function CloseUnit({ unit, onClose }: { unit: StructureUnit; onClose: (unitId: string) => Promise<void> }) {
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  return (
    <div style="margin-top:16px">
      {!asking
        ? <button class="link" onClick={() => setAsking(true)}>Close this {KIND[unit.kind].toLowerCase()}</button>
        : (
          <div class="inline-form">
            <div class="s">{unit.name} and anything under it come off the tree. What was done on it keeps its place in the record. Open work has to be moved or closed first.</div>
            <div class="buttons">
              <button class="btn" disabled={busy} onClick={() => { setBusy(true); void onClose(unit.id).finally(() => { setBusy(false); setAsking(false); }); }}>Close it</button>
              <button class="link" onClick={() => setAsking(false)}>Keep it</button>
            </div>
          </div>
        )}
    </div>
  );
}

function RolePick({ label, value, people, optional, onSave }: { label: string; value: string; people: { upn: string; name: string }[]; optional?: boolean; onSave: (upn: string) => Promise<void> }) {
  const [upn, setUpn] = useState(value);
  const [busy, setBusy] = useState(false);
  const save = async () => { if (!upn && !optional) return; setBusy(true); try { await onSave(upn); } finally { setBusy(false); } };
  return (
    <div class="inline-form" style="margin-bottom:10px">
      <div class="s">{label}{optional ? " · optional" : ""}</div>
      <Picker label={label} options={peopleOptions(people)} value={upn} onChange={setUpn} clearable={optional} placeholder={optional ? "Nobody" : "Search people…"} />
      {upn !== value && (upn || optional) && <button class="btn" disabled={busy} onClick={() => void save()}>{upn ? "Save" : "Clear"}</button>}
    </div>
  );
}

function AddPerson({ people, onAdd }: { people: { upn: string; name: string }[]; onAdd: (upn: string) => Promise<void> }) {
  const [upn, setUpn] = useState("");
  const [busy, setBusy] = useState(false);
  if (!people.length) return <div class="s">Everyone on the program is already on this unit.</div>;
  return (
    <div class="inline-form" style="margin:12px 0">
      <span class="label">Add person</span>
      <Picker label="Add person" options={peopleOptions(people)} value={upn} onChange={setUpn} placeholder="Search people…" />
      <button class="btn" disabled={busy || !upn} onClick={() => { setBusy(true); void onAdd(upn).finally(() => setBusy(false)); }}>Add</button>
    </div>
  );
}

function AddChild({ kind, parentId, people, onCreate }: { kind: "workstream" | "team"; parentId: string; people: { upn: string; name: string }[]; onCreate: StructureActions["onCreate"] }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [dl, setDl] = useState("");
  const [tl, setTl] = useState("");
  const [busy, setBusy] = useState(false);
  if (!open) return <button class="link" style="margin-top:10px" onClick={() => setOpen(true)}>Add {kind}</button>;
  return (
    <div class="inline-form" style="margin-top:12px">
      <span class="label">Add {kind}</span>
      <input type="text" value={name} placeholder="Name" onInput={(e) => setName((e.target as HTMLInputElement).value)} />
      <Picker label="Delivery lead" options={peopleOptions(people)} value={dl} onChange={setDl} placeholder="Delivery lead…" />
      <Picker label="Technical lead" options={peopleOptions(people)} value={tl} onChange={setTl} clearable placeholder="Technical lead (optional)" />
      <div class="buttons">
        <button class="btn" disabled={busy || !name.trim() || !dl} onClick={() => { setBusy(true); void onCreate({ kind, parentId, name: name.trim(), deliveryLead: dl, ...(tl ? { technicalLead: tl } : {}) }).finally(() => { setBusy(false); setOpen(false); setName(""); }); }}>Create</button>
        <button class="link" onClick={() => setOpen(false)}>Cancel</button>
      </div>
    </div>
  );
}

function MoveClin({ unitName, clins, currentParent, onMove }: { unitName: string; clins: StructureUnit[]; currentParent: string | null; onMove: (clinId: string) => Promise<void> }) {
  const [clinId, setClinId] = useState(clins.find((c) => c.id !== currentParent)?.id ?? "");
  const [busy, setBusy] = useState(false);
  const [ask, setAsk] = useState(false);
  const dest = clins.find((c) => c.id === clinId);
  if (!dest) return null;
  return (
    <div class="inline-form" style="margin-top:12px">
      <span class="label">Move to a CLIN</span>
      <Picker label="Destination CLIN" options={clins.filter((c) => c.id !== currentParent).map((c) => ({ value: c.id, label: c.name }))} value={clinId} onChange={setClinId} placeholder="Search CLINs…" />
      {!ask
        ? <button class="link" onClick={() => setAsk(true)}>Move…</button>
        : <div class="buttons">
            <button class="btn" disabled={busy} onClick={() => { setBusy(true); void onMove(clinId).finally(() => { setBusy(false); setAsk(false); }); }}>{unitName} will be closed and opened under {dest.name}. History stays on the old unit.</button>
            <button class="link" onClick={() => setAsk(false)}>Cancel</button>
          </div>}
    </div>
  );
}

/**
 * The tree, drawn. A list nested in a list, with the connectors in CSS: nested
 * boxes read as indentation, not as who reports to whom.
 */
function Org({ units, lineOf }: { units: StructureUnit[]; lineOf: (u: StructureUnit) => { key: string; tone: "a" | "b" } | undefined }) {
  const children = (id: string | null) => units.filter((u) => u.parentId === id);
  const Node = ({ u }: { u: StructureUnit }) => {
    const kids = children(u.id);
    const needsLead = u.kind === "workstream" || u.kind === "team";
    // A CLIN and Unassigned carry no lead, so they say nothing rather than a dash.
    const lead = u.deliveryLead?.name ?? (needsLead ? "No delivery lead" : undefined);
    const line = lineOf(u);
    return (
      <li>
        <div class={`org-card${needsLead && !u.deliveryLead ? " gap" : ""}`}>
          <div class="org-kind">
            {/* "Unassigned" is both a kind and the unit's name; printing it twice reads as a fault. */}
            {u.kind !== "unassigned" ? <span class="s">{KIND[u.kind]}</span> : <span class="s">Program</span>}
            {/* Which line pays for it, said in the number as well as the hue. */}
            {needsLead && <span class={`clin-tag${line ? ` ${line.tone}` : ""}`}>{line ? line.key : "No line"}</span>}
          </div>
          <div class="org-name">{u.name}</div>
          {lead && <div class="s">{lead}</div>}
          <div class="s">{plural(u.people, "person", "people")}</div>
        </div>
        {kids.length > 0 && <ul>{kids.map((c) => <Node key={c.id} u={c} />)}</ul>}
      </li>
    );
  };
  const roots = units.filter((u) => !u.parentId);
  if (roots.length === 0) return <div class="state muted">No units yet.</div>;
  return (
    <div class="org">
      <ul>{roots.map((u) => <Node key={u.id} u={u} />)}</ul>
    </div>
  );
}
